'use client';

import { useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { useEffect, useState } from 'react';

export interface LocationSearchResult {
  center?: google.maps.LatLngLiteral | null;
  bounds?: google.maps.LatLngBoundsLiteral | null;
  location: {
    suburb: string | null;
    state: string | null;
    postcode: string | null;
  };
}

export interface PlaceAutocompleteCallback {
  (data: LocationSearchResult | null, place?: google.maps.places.PlaceResult | null): void;
}

function extractLocationDetails(addressComponents?: google.maps.GeocoderAddressComponent[]): {
  suburb: string | null;
  state: string | null;
  postcode: string | null;
} {
  let suburb = null;
  let state = null;
  let postcode = null;

  addressComponents?.forEach((component) => {
    if (component.types.includes('locality')) {
      suburb = component.long_name;
    }
    if (component.types.includes('administrative_area_level_1')) {
      state = component.short_name;
    }
    if (component.types.includes('postal_code')) {
      postcode = component.long_name;
    }
  });

  return { suburb, state, postcode };
}

// export type AutocompleteOptions = google.maps.places.AutocompleteOptions;

const defaultOptions: google.maps.places.AutocompleteOptions = {
  fields: ['geometry', 'name', 'formatted_address', 'address_components'],
  componentRestrictions: { country: 'au' },
  // restrict types to town suburbs cities and states
  types: ['(regions)'],
};

export interface PlaceAutocompleteOwnOptions {
  onSelect: PlaceAutocompleteCallback;
  options?: google.maps.places.AutocompleteOptions;
}

export interface UsePlaceAutocompleteProps extends PlaceAutocompleteOwnOptions {
  ref?: React.ForwardedRef<HTMLInputElement>;
}

export interface UsePlaceAutocompleteReturn {
  // inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  inputProps: React.ComponentPropsWithRef<'input'>;
  placeAutocomplete: google.maps.places.Autocomplete | null;
  clearValue: () => void;
}

export default function usePlaceAutocomplete(
  props: UsePlaceAutocompleteProps,
): UsePlaceAutocompleteReturn {
  const { ref: forwardedRef, onSelect, options: customOptions } = props;
  const options = React.useMemo(() => ({ ...defaultOptions, ...customOptions }), [customOptions]);

  const ref = React.useRef<HTMLInputElement>(null);

  React.useImperativeHandle(forwardedRef, () => ref.current!);

  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);

  const places = useMapsLibrary('places');

  const clearValue = () => {
    if (!ref?.current) return;

    ref.current.value = '';
    onSelect(null);
  };

  useEffect(() => {
    if (!places || !ref?.current) return;
    setPlaceAutocomplete(new places.Autocomplete(ref.current, options));
  }, [options, places, ref]);

  useEffect(() => {
    const element = ref?.current;

    if (!element) return;

    const handleNativeKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        // Prevent form submit, but allow Google to still get the keypress
        // We don't call stopPropagation or prevent default here
        const form = (e.target as HTMLElement).closest('form');
        if (form) {
          e.preventDefault();
        }
      }
    };

    element.addEventListener('keydown', handleNativeKeyDown);

    return () => {
      element.removeEventListener('keydown', handleNativeKeyDown);
    };
  }, [ref]);

  const placeListener = React.useCallback(() => {
    if (!placeAutocomplete) return;

    const place = placeAutocomplete.getPlace();

    const latLng = place.geometry?.location?.toJSON();
    const viewport = place.geometry?.viewport?.toJSON();

    onSelect(
      {
        center: latLng,
        bounds: viewport,
        location: extractLocationDetails(place.address_components),
      },
      place,
    );
  }, [onSelect, placeAutocomplete]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    const removeListener = placeAutocomplete.addListener('place_changed', placeListener);

    return () => {
      google.maps.event.removeListener(removeListener);
    };
  }, [placeAutocomplete, placeListener]);

  const inputProps: React.ComponentPropsWithRef<'input'> = {
    ref,
  };

  return {
    inputProps,
    clearValue,
    placeAutocomplete,
  };
}
